import { Language } from '@eneco/common/application';
import type { GetOrderResponse } from '@eneco/dc-be/client';
import { SimpleOrderBasketLineItemDto } from '@eneco/dc-be/client/types.gen';

import { saveOrderManagementState } from './orderManagementStorage';
import { initiateOrder, updateOrderBasket } from '../steps/selectedProductsOverview/initiateOrder';

export interface SyncOrderBasketOrInitiateParams {
  orderId?: string;
  orderData?: GetOrderResponse | null;
  boilerMaintenance?: string;
  serviceProposal?: string;
  accountNumber: string;
  language: Language;
  priceSimulationId?: string;
  electricityProductTechnicalName?: string;
  gasProductTechnicalName?: string;
  promoId?: string;
}

/**
 * Updates the order basket with current service selections, or initiates a new order when no orderId.
 * Returns the new orderId if an order was created, otherwise null.
 * Callers with orderId should pass orderData from usePublicDCBE(getEnecoBeXapiSiteApiV1OrdersByOrderId).
 */
export const syncOrderBasketOrInitiate = async (params: SyncOrderBasketOrInitiateParams): Promise<string | null> => {
  const {
    orderId,
    orderData,
    boilerMaintenance,
    serviceProposal,
    accountNumber,
    language,
    priceSimulationId,
    electricityProductTechnicalName,
    gasProductTechnicalName,
    promoId,
  } = params;

  if (orderId) {
    saveOrderManagementState({ orderId });
    const existingItems = orderData?.basket?.items ?? [];
    const hasNewProductSelection = !!(electricityProductTechnicalName || gasProductTechnicalName);
    const commodityItems: SimpleOrderBasketLineItemDto[] = hasNewProductSelection
      ? [
          ...(electricityProductTechnicalName
            ? [{ technicalProductName: electricityProductTechnicalName, orderProductType: 'Commodity' as const }]
            : []),
          ...(gasProductTechnicalName
            ? [{ technicalProductName: gasProductTechnicalName, orderProductType: 'Commodity' as const }]
            : []),
        ]
      : existingItems.filter(item => item.orderProductType === 'Commodity');
    const hasNewServiceSelection = !!(boilerMaintenance || serviceProposal);
    const serviceItems: SimpleOrderBasketLineItemDto[] = hasNewServiceSelection
      ? [
          ...(serviceProposal && serviceProposal !== 'none'
            ? [{ technicalProductName: serviceProposal, orderProductType: 'NonCommodity' as const }]
            : []),
          ...(boilerMaintenance && boilerMaintenance !== 'none'
            ? [{ technicalProductName: boilerMaintenance, orderProductType: 'NonCommodity' as const }]
            : []),
        ]
      : existingItems.filter(item => item.orderProductType === 'NonCommodity');
    await updateOrderBasket({ orderId, priceSimulationId, items: [...commodityItems, ...serviceItems] });
    return null;
  }

  const newOrderId = await initiateOrder({
    accountNumber,
    language,
    priceSimulationId,
    boilerMaintenance,
    serviceProposal,
    electricityProductTechnicalName,
    gasProductTechnicalName,
    promoId,
  });
  saveOrderManagementState({ orderId: newOrderId });
  return newOrderId;
};
